import { Check, Loader2, Lock } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { formatPrice, getTemplatePrice } from '../../../utils/format';
import { EYEBROW, FOCUS_RING } from '../constants';
import { PanelHeading } from '../controls';

const thumbOf = (t) => t.thumbnail_image || t.background_image;

/**
 * Background tool: switch to another design from the same category while
 * keeping all text and elements. `variant="row"` is the compact strip used in
 * Simple mode.
 */
export const BackgroundPanel = ({
  templates,
  loading,
  currentId,
  currentTemplate,
  onPick,
  lockedReason,
  tintControls,
  variant = 'panel',
}) => {
  const list = templates.some((t) => t.id === currentId) || !currentTemplate ? templates : [currentTemplate, ...templates];
  const currentPrice = getTemplatePrice(currentTemplate);

  const tiles = list.map((t) => {
    const on = t.id === currentId;
    const price = getTemplatePrice(t);
    return (
      <button
        key={t.id}
        type="button"
        onClick={() => !on && onPick(t)}
        disabled={Boolean(lockedReason) && !on}
        aria-pressed={on}
        aria-label={`${t.background_code ? `Background ${t.background_code}` : t.name}${on ? ' (current design)' : ''}${price !== currentPrice ? `, ${formatPrice(price)}` : ''}`}
        className={cn(
          'group relative flex flex-col gap-1 text-left rounded-lg disabled:opacity-50 disabled:cursor-not-allowed',
          variant === 'row' ? 'w-[76px] shrink-0' : '',
          FOCUS_RING
        )}
        data-testid={`bg-${t.id}`}
      >
        <span
          className={cn(
            'relative block aspect-[8.5/11] w-full rounded-md overflow-hidden bg-cream',
            on ? 'ring-2 ring-ink ring-offset-2 ring-offset-white' : 'border border-[#E6E1D6] group-hover:border-[#B8B2A5]'
          )}
        >
          {thumbOf(t) ? <img src={thumbOf(t)} alt="" loading="lazy" className="h-full w-full object-cover" /> : null}
          {on && (
            <span className="absolute top-1 right-1 h-5 w-5 rounded-full bg-ink text-paper flex items-center justify-center">
              <Check className="h-3 w-3" aria-hidden="true" />
            </span>
          )}
        </span>
        {variant === 'panel' && (
          <span className="text-[11px] font-bold text-ink leading-tight line-clamp-2">
            {t.background_code ? `Background ${t.background_code}` : t.name}
            {price !== currentPrice ? <span className="block font-semibold text-[#6B665C]">{formatPrice(price)}</span> : null}
          </span>
        )}
      </button>
    );
  });

  if (variant === 'row') {
    if (!loading && list.length < 2) return null;
    return (
      <div className="flex flex-col gap-2.5" data-testid="simple-designs">
        <span className="text-[13px] font-extrabold text-ink">Change design</span>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-[#6B665C]" aria-label="Loading designs" />
        ) : (
          <div className="flex gap-2.5 overflow-x-auto pb-1 -mx-1 px-1 pt-1" role="group" aria-label="Designs">
            {tiles}
          </div>
        )}
        {lockedReason ? <p className="text-xs text-[#6B665C]">{lockedReason}</p> : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3.5" data-testid="background-panel">
      <PanelHeading>Background</PanelHeading>
      <p className="text-[13px] leading-relaxed text-[#4A4640]">
        Switch to another design from this collection. All your text, shapes and photos stay exactly where they are.
      </p>
      {currentTemplate && (
        <div className="rounded-xl bg-cream px-3 py-2.5 text-[13px] text-ink flex justify-between gap-2">
          <span className="font-bold truncate">{currentTemplate.name}</span>
          <span className="font-semibold text-[#4A4640]">{formatPrice(currentPrice)}</span>
        </div>
      )}
      {tintControls}
      {lockedReason && (
        <p className="text-[13px] text-[#4A4640] flex gap-2 items-start">
          <Lock className="h-4 w-4 mt-0.5 shrink-0 text-[#6E5B1C]" aria-hidden="true" />
          {lockedReason}
        </p>
      )}
      <p className={EYEBROW}>Designs in this collection</p>
      {loading ? (
        <p className="text-sm text-[#6B665C] flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading designs…
        </p>
      ) : list.length < 2 ? (
        <p className="text-sm text-[#6B665C]">There are no other designs in this collection yet.</p>
      ) : (
        <div className="grid grid-cols-3 gap-x-2 gap-y-3">{tiles}</div>
      )}
    </div>
  );
};

export default BackgroundPanel;
